import { every, forEach } from "lodash";
import { Button } from "PFComponents/button";
import { DataTableColumn, Warning } from "PFComponents/data_table/data_table";
import Toggle from "PFComponents/toggle/toggle";
import useForceUpdate from "PFCore/helpers/use_force_update";
import { Permission, PermissionsGroup } from "PFTypes";
import React from "react";
import { useTranslation } from "react-i18next";

import { NameColumn } from "./columns/name_column";

export type PermissionGroupSketch = Omit<PermissionsGroup, "id"> & {
  id: number | "self";
};

type UseFunctionalityPermissions = {
  permissionsGroup: PermissionGroupSketch;
};

type UseFunctionalityPermissionsReturn = {
  columns: DataTableColumn[];
  warnings: Warning[];
};

export const useFunctionalityPermissions = ({
  permissionsGroup
}: UseFunctionalityPermissions): UseFunctionalityPermissionsReturn => {
  const forceUpdate = useForceUpdate();
  const { t } = useTranslation(["bookingModule", "admin"]);
  const bookingReporterWarningMessage = t("bookingModule:config.permissions.bookingReporterWarning");
  const warnings = [
    {
      key: "booking_reporter",
      message: bookingReporterWarningMessage
    }
  ];

  //write here a condition for read only permission (you can pass permission as a parameter)
  const isPermissionReadOnly = () => false;

  const getToggleColumn = () => ({
    narrow: true,
    renderHeader: () => (
      <Button
        kind="tertiary"
        small
        onClick={() => {
          const items = permissionsGroup.permissions;
          const allPermitted = every(items, { permitted: true });
          forEach(items, (item) => (item.permitted = !allPermitted));
          forceUpdate();
        }}
      >
        {t("admin:permissionsGroups.permissions.toggleAll")}
      </Button>
    ),
    renderCell: (permission: Permission) => (
      <Toggle
        readOnly={isPermissionReadOnly()}
        checked={permission.permitted}
        onChange={(value) => {
          permission.permitted = value;
          forceUpdate();
        }}
      />
    )
  });

  const columns = [
    getToggleColumn(),
    {
      headerName: t("admin:permissionsGroups.permissions.headerName"),
      renderCell: (permission: Permission) => (
        <NameColumn name={permission.name} readOnly={isPermissionReadOnly()} />
      )
    },
    { headerName: t("admin:permissionsGroups.permissions.headerKey"), cellKey: "name" }
  ];

  return {
    columns,
    warnings
  };
};
